// @ts-strict-ignore
import React, {
  useCallback, useEffect, useState,
} from 'react';
import { useSnackBar } from '@usealloy/component-library';
import cloneDeep from 'lodash/cloneDeep';

import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import ReviewWorkflowImportModal from './review/ReviewWorkflowImport';
import {
  checkExistenceOfDataPassingActions,
  getAllRelevantSupplementalData,
  getSuccessMsg,
  scrubServiceCredentials,
} from './helpers';
import type { MissingRequiredItemInfo, PendingImportData } from './types';
import { WorkflowJSONType } from './types';

import { importPartialWorkflowData, importWorkflowData } from '../../../store/redux/slice';
import { generateImportableWorkflowJson, partialImportIntoWorkflowJson } from '../../../store/engine/importer';
import { selectWorkflowData, selectWorkflowProps } from '../../../store/redux/selectors';
import { mergePendingRules } from '../../../store/engine/importer/merge';
import { useFeatureFlags } from '~/dashboard/hooks';

type MagicImportProps = {
  isCanvasFocused: boolean;
};
export default function MagicImport({ isCanvasFocused }: MagicImportProps) {
  const isWorkflowLevelCopyPasteEnabled = useFeatureFlags('workflowLevelCopyPaste');

  const dispatch = useAppDispatch();
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);

  const { token: workflowToken } = workflowData;
  const addSnackBar = useSnackBar();

  const [
    isResolveImportIssuesModalOpen,
    setIsResolveImportIssuesModalOpen,
  ] = useState<boolean>(false);
  const [
    missingRequiredItemsForImport,
    setMissingRequiredItemsForImport,
  ] = useState<MissingRequiredItemInfo>([]);
  const [
    pendingImportData,
    setPendingImportData,
  ] = useState<PendingImportData>(undefined);

  const onSuccessfulImport = useCallback((
    type: WorkflowJSONType,
    isContainingDataPassingNodes: boolean,
  ) => {
    addSnackBar({
      duration: 5000,
      title: getSuccessMsg(type, isContainingDataPassingNodes),
      type: 'success',
    });
  }, [addSnackBar]);

  const onCopyKeyboardShortcutDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyC' && isCanvasFocused) {
      try {
        const { actions, thresholds, dataRoutes } = workflowData;
        const {
          services,
          reasonCodes,
          reasonCodeGroups,
        } = workflowProps;

        const isContainingDataPassingNodes = checkExistenceOfDataPassingActions(actions);

        const supplementalData = getAllRelevantSupplementalData({
          actions,
          thresholds,
          dataRoutes,
          services,
          reasonCodes,
          reasonCodeGroups,
        });

        // @todo - we're disabling this for now
        /* if (focusedAction) {
          // copy only the data related to the focused action, if any
          dataToCopy = getSelectedActionAndRelevantData({
            selectedNode: focusedAction.publicId,
            workflowData,
          });
          snackBarMessage = `Successfully copied ${getActionName(focusedAction)} to the clipboard.`;
        } */
        // copy the whole workflow
        const { actionsWithNoCredentials, impactedServiceActions } = scrubServiceCredentials({
          actions: cloneDeep(workflowData.actions),
          services,
        });
        const dataToCopy = {
          ...workflowData,
          actions: actionsWithNoCredentials,
        };
        const snackBarMessage = getSuccessMsg('copy', isContainingDataPassingNodes);

        navigator.clipboard.writeText(JSON.stringify({
          ...dataToCopy,
          serviceAttributeData: supplementalData.serviceAttributes,
          reasonCodeGroupData: supplementalData.reasonCodeGroups,
        }));

        addSnackBar({
          duration: 5000,
          title: snackBarMessage,
          type: 'success',
        });

        if (impactedServiceActions?.length > 0) {
          addSnackBar({
            duration: 5000,
            title: `The service credentials in the following service nodes are removed in the workflow copied in the clipboard: ${impactedServiceActions.join(', ')}`,
            type: 'success',
          });
        }
      } catch (error) {
        captureExceptionsToSentry(error);
        addSnackBar({
          duration: 2000,
          title: 'Something went wrong while copying the workflow data.',
          type: 'danger',
        });
      }
    }
  }, [addSnackBar, isCanvasFocused, workflowData, workflowProps]);

  const onPasteKeyboardShortcutDown = useCallback(async (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyV' && isCanvasFocused) {
      const workflowDataInClipboard = await navigator.clipboard.readText();
      if (workflowDataInClipboard) {
        try {
          const jsonParsedWorkflowData = JSON.parse(workflowDataInClipboard);
          const {
            services,
            tags,
            outcomes,
            reasonCodeGroups,
            lists,
          } = workflowProps;

          if (jsonParsedWorkflowData?.partial) {
            const {
              actions,
              thresholds,
              dataRoutes,
              identifiedObjectsMissingInTheOrganization,
              rulesPendingMerge,
              expressionsPendingMerge,
              issues,
            } = partialImportIntoWorkflowJson({
              jsonData: jsonParsedWorkflowData,
              workflowData,
              services,
              tags,
              outcomes,
              reasonCodeGroups,
              lists,
            });

            const isContainingDataPassingNodes = checkExistenceOfDataPassingActions(actions);

            if (identifiedObjectsMissingInTheOrganization.length) {
              setPendingImportData({
                actions,
                thresholds,
                dataRoutes,
                rulesPendingMerge,
                expressionsPendingMerge,
                jsonData: jsonParsedWorkflowData,
                validations: {
                  actions: issues?.actions || [],
                  thresholds: [],
                },
                type: WorkflowJSONType.PARTIAL,
              });
              setMissingRequiredItemsForImport(identifiedObjectsMissingInTheOrganization);
              setIsResolveImportIssuesModalOpen(true);
            } else {
              const {
                actions: actionsWithDeepMergedRules,
                actionsWithUpdatedRules,
              } = mergePendingRules({
                actions: { ...actions, ...cloneDeep(workflowData.actions) },
                thresholds: { ...thresholds, ...cloneDeep(workflowData.thresholds) },
                dataRoutes: { ...dataRoutes, ...cloneDeep(workflowData.dataRoutes) },
                jsonData: jsonParsedWorkflowData,
                pendingRules: rulesPendingMerge,
                pendingExpressions: expressionsPendingMerge,
                services,
                reasonCodeGroups,
              });

              dispatch(importPartialWorkflowData({
                actions: actionsWithDeepMergedRules,
                thresholds,
                dataRoutes,
                validations: {
                  actions: [...issues?.actions || [], ...actionsWithUpdatedRules],
                  thresholds: [],
                },
              }));
              onSuccessfulImport(WorkflowJSONType.PARTIAL, isContainingDataPassingNodes);
            }
          } else {
            const {
              actions,
              thresholds,
              dataRoutes,
              dependencies,
              identifiedObjectsMissingInTheOrganization,
              issues,
            } = generateImportableWorkflowJson({
              jsonData: jsonParsedWorkflowData,
              services,
              tags,
              outcomes,
              reasonCodeGroups,
              lists,
            });

            const isContainingDataPassingNodes = checkExistenceOfDataPassingActions(actions);
            if (identifiedObjectsMissingInTheOrganization.length) {
              setPendingImportData({
                actions,
                thresholds,
                dataRoutes,
                dependencies,
                validations: issues,
                type: WorkflowJSONType.FULL,
              });
              setMissingRequiredItemsForImport(identifiedObjectsMissingInTheOrganization);
              setIsResolveImportIssuesModalOpen(true);
            } else {
              dispatch(importWorkflowData({
                actions,
                thresholds,
                dataRoutes,
                dependencies,
                validations: issues,
                workflowToken,
              }));
              onSuccessfulImport(WorkflowJSONType.FULL, isContainingDataPassingNodes);
            }
          }
        } catch (error) {
          addSnackBar({
            duration: 2000,
            title: 'Something went wrong while importing the workflow data from your clipboard.',
            type: 'danger',
          });
        }
      }
    }
  }, [
    addSnackBar,
    dispatch,
    isCanvasFocused,
    onSuccessfulImport,
    workflowData,
    workflowProps,
    workflowToken,
  ]);

  useEffect(() => {
    if (isWorkflowLevelCopyPasteEnabled) {
      window.addEventListener('keydown', onCopyKeyboardShortcutDown);
      window.addEventListener('keydown', onPasteKeyboardShortcutDown);
    }

    return () => {
      if (isWorkflowLevelCopyPasteEnabled) {
        window.removeEventListener('keydown', onCopyKeyboardShortcutDown);
        window.removeEventListener('keydown', onPasteKeyboardShortcutDown);
      }
    };
  }, [isWorkflowLevelCopyPasteEnabled, onCopyKeyboardShortcutDown, onPasteKeyboardShortcutDown]);

  return (
    <ReviewWorkflowImportModal
      isOpen={isResolveImportIssuesModalOpen}
      missingRequiredItemsForImport={missingRequiredItemsForImport}
      pendingImportData={pendingImportData}
      onSuccessfulImport={onSuccessfulImport}
      closeModal={() => setIsResolveImportIssuesModalOpen(false)}
    />
  );
}

